import { Injectable } from '@angular/core';
import { EesDesService } from './ees-des.service';

@Injectable()
export class AuthSessionService {

  private readonly TEMP_TOKEN_KEY = 'temporary_user_token';
  private readonly TEMP_TOKEN_EXPIRY_KEY = 'temporary_user_token_expiry';
  private readonly TEMP_TOKEN_VALIDITY_SECONDS_KEY = 'temporary_token_validity_seconds';
  private readonly TEMP_TOKEN_VALIDITY_MS = 5 * 60 * 1000;
  private readonly TEMP_TOKEN_MAX_VALIDITY_MS = 30 * 60 * 1000;
  private readonly COMPANY_TOKENS_CACHE_KEY = 'company_api_tokens_cache';
  private readonly SESSION_USER_TOKEN_KEY = 'user_access_token';
  private readonly SESSION_USER_TOKEN_PLAIN_KEY = 'user_session_token_plain';
  private readonly ADMIN_COMPANY_TOKEN_KEY = 'administrator_company_token';

  private companyTokensById: { [companyId: string]: string } = {};
  private temporaryUserTokenInMemory: string = '';
  private temporaryUserTokenExpiryInMemory: number = 0;
  private temporaryTokenValiditySecondsInMemory: number = 0;
  private sessionUserTokenInMemory: string = '';
  private administratorCompanyTokenInMemory: string = '';

  constructor(private eesdesService: EesDesService) {
    this.loadCompanyTokensCache();
    this.restoreTemporaryTokenFromSession();
    this.restoreSessionUserToken();
    this.restoreAdministratorCompanyToken();
  }

  setTemporaryUserToken(token: string, validityMs?: number, originalValiditySeconds?: number, absoluteExpiryMs?: number): void {
    if (!token) {
      return;
    }
    let validity = validityMs ? validityMs : this.TEMP_TOKEN_VALIDITY_MS;
    if (validity > this.TEMP_TOKEN_MAX_VALIDITY_MS) {
      validity = this.TEMP_TOKEN_MAX_VALIDITY_MS;
    }
    if (validity <= 0) {
      validity = this.TEMP_TOKEN_VALIDITY_MS;
    }
    let expiry = (absoluteExpiryMs && absoluteExpiryMs > Date.now())
      ? absoluteExpiryMs
      : (Date.now() + validity);
    let validitySeconds = (originalValiditySeconds && originalValiditySeconds > 0)
      ? originalValiditySeconds
      : Math.round(validity / 1000);
    this.temporaryUserTokenInMemory = token;
    this.temporaryUserTokenExpiryInMemory = expiry;
    this.temporaryTokenValiditySecondsInMemory = validitySeconds;
    // sessionStorage survives localStorage.clear() used on company list page
    sessionStorage.setItem(this.TEMP_TOKEN_KEY, token);
    sessionStorage.setItem(this.TEMP_TOKEN_EXPIRY_KEY, expiry.toString());
    sessionStorage.setItem(this.TEMP_TOKEN_VALIDITY_SECONDS_KEY, validitySeconds.toString());
    try {
      if (!localStorage.getItem('is_enc_on')) {
        localStorage.setItem('is_enc_on', this.eesdesService.set('1', 'is_enc_on'));
      }
      localStorage.setItem(this.TEMP_TOKEN_KEY, this.eesdesService.set(token, 'is_login_psw'));
      localStorage.setItem(this.TEMP_TOKEN_EXPIRY_KEY, expiry.toString());
      localStorage.setItem(this.TEMP_TOKEN_VALIDITY_SECONDS_KEY, validitySeconds.toString());
    } catch (e) {
    }
  }

  getTemporaryTokenExpiryMs(): number {
    if (this.temporaryUserTokenExpiryInMemory > 0) {
      return this.temporaryUserTokenExpiryInMemory;
    }
    let raw = sessionStorage.getItem(this.TEMP_TOKEN_EXPIRY_KEY) || localStorage.getItem(this.TEMP_TOKEN_EXPIRY_KEY);
    let expiry = raw ? parseInt(raw, 10) : 0;
    return !isNaN(expiry) && expiry > 0 ? expiry : 0;
  }

  getTemporaryUserToken(): string {
    if (!this.isTemporaryTokenValid()) {
      this.clearTemporaryUserToken();
      return '';
    }
    if (this.temporaryUserTokenInMemory) {
      return this.temporaryUserTokenInMemory;
    }
    let sessionToken = sessionStorage.getItem(this.TEMP_TOKEN_KEY);
    if (sessionToken) {
      this.temporaryUserTokenInMemory = sessionToken;
      return sessionToken;
    }
    try {
      let token = this.eesdesService.get(this.TEMP_TOKEN_KEY, localStorage.getItem(this.TEMP_TOKEN_KEY));
      if (token) {
        this.temporaryUserTokenInMemory = token;
        sessionStorage.setItem(this.TEMP_TOKEN_KEY, token);
        return token;
      }
    } catch (e) {
    }
    return '';
  }

  isTemporaryTokenValid(): boolean {
    let expiryValue = this.temporaryUserTokenExpiryInMemory
      ? this.temporaryUserTokenExpiryInMemory.toString()
      : (sessionStorage.getItem(this.TEMP_TOKEN_EXPIRY_KEY) || localStorage.getItem(this.TEMP_TOKEN_EXPIRY_KEY));
    let storedToken = this.temporaryUserTokenInMemory
      || sessionStorage.getItem(this.TEMP_TOKEN_KEY)
      || localStorage.getItem(this.TEMP_TOKEN_KEY);
    if (!expiryValue || !storedToken) {
      return false;
    }
    let expiry = parseInt(expiryValue, 10);
    if (isNaN(expiry)) {
      return false;
    }
    this.temporaryUserTokenExpiryInMemory = expiry;
    return Date.now() < expiry;
  }

  getTemporaryTokenRemainingMs(): number {
    if (!this.isTemporaryTokenValid()) {
      return 0;
    }
    let expiryValue = this.temporaryUserTokenExpiryInMemory
      ? this.temporaryUserTokenExpiryInMemory
      : parseInt(sessionStorage.getItem(this.TEMP_TOKEN_EXPIRY_KEY) || localStorage.getItem(this.TEMP_TOKEN_EXPIRY_KEY), 10);
    let remaining = expiryValue - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  /** Original validity from login API temporary_token_validity_seconds (ms) */
  getTemporaryTokenValidityMs(): number {
    let seconds = this.getTemporaryTokenValiditySeconds();
    return seconds > 0 ? seconds * 1000 : this.TEMP_TOKEN_VALIDITY_MS;
  }

  getTemporaryTokenValiditySeconds(): number {
    if (this.temporaryTokenValiditySecondsInMemory > 0) {
      return this.temporaryTokenValiditySecondsInMemory;
    }
    let raw = sessionStorage.getItem(this.TEMP_TOKEN_VALIDITY_SECONDS_KEY)
      || localStorage.getItem(this.TEMP_TOKEN_VALIDITY_SECONDS_KEY);
    let seconds = raw ? parseInt(raw, 10) : 0;
    if (!isNaN(seconds) && seconds > 0) {
      this.temporaryTokenValiditySecondsInMemory = seconds;
      return seconds;
    }
    return Math.round(this.TEMP_TOKEN_VALIDITY_MS / 1000);
  }

  clearTemporaryUserToken(): void {
    this.temporaryUserTokenInMemory = '';
    this.temporaryUserTokenExpiryInMemory = 0;
    this.temporaryTokenValiditySecondsInMemory = 0;
    sessionStorage.removeItem(this.TEMP_TOKEN_KEY);
    sessionStorage.removeItem(this.TEMP_TOKEN_EXPIRY_KEY);
    sessionStorage.removeItem(this.TEMP_TOKEN_VALIDITY_SECONDS_KEY);
    localStorage.removeItem(this.TEMP_TOKEN_KEY);
    localStorage.removeItem(this.TEMP_TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.TEMP_TOKEN_VALIDITY_SECONDS_KEY);
  }

  private restoreTemporaryTokenFromSession(): void {
    let sessionToken = sessionStorage.getItem(this.TEMP_TOKEN_KEY);
    let sessionExpiry = sessionStorage.getItem(this.TEMP_TOKEN_EXPIRY_KEY);
    let sessionValidity = sessionStorage.getItem(this.TEMP_TOKEN_VALIDITY_SECONDS_KEY);
    if (sessionToken && sessionExpiry && Date.now() < parseInt(sessionExpiry, 10)) {
      this.temporaryUserTokenInMemory = sessionToken;
      this.temporaryUserTokenExpiryInMemory = parseInt(sessionExpiry, 10);
      if (sessionValidity) {
        let seconds = parseInt(sessionValidity, 10);
        if (!isNaN(seconds) && seconds > 0) {
          this.temporaryTokenValiditySecondsInMemory = seconds;
        }
      }
    }
  }

  extractTemporaryTokenFromLoginResponse(data: any): string {
    if (!data) {
      return '';
    }
    if (data.temporary_user_token) {
      return data.temporary_user_token;
    }
    if (data.temporary_token) {
      return data.temporary_token;
    }
    if (data.temp_user_token) {
      return data.temp_user_token;
    }
    if (typeof data.user_token === 'string' && data.user_token) {
      return data.user_token;
    }
    if (data.user) {
      if (data.user.temporary_user_token) {
        return data.user.temporary_user_token;
      }
      if (data.user.temporary_token) {
        return data.user.temporary_token;
      }
      if (typeof data.user.user_token === 'string' && data.user.user_token) {
        return data.user.user_token;
      }
    }
    return '';
  }

  extractCompanyTokenFromLoginResponse(data: any): string {
    if (!data) {
      return '';
    }
    if (data.company_token) {
      return data.company_token;
    }
    if (data.user && data.user.company_token) {
      return data.user.company_token;
    }
    return '';
  }

  storeTemporaryTokenFromLoginResponse(data: any): void {
    let token = this.extractTemporaryTokenFromLoginResponse(data);
    // First login company-select must use temp token, not any old session token
    localStorage.removeItem('user_access_token');
    sessionStorage.removeItem(this.SESSION_USER_TOKEN_PLAIN_KEY);
    this.sessionUserTokenInMemory = '';
    if (!token) {
      this.storeCompanyTokenFromLoginResponse(data);
      return;
    }
    let validityMs = this.TEMP_TOKEN_VALIDITY_MS;
    let validitySeconds = data.temporary_token_validity_seconds;
    if ((validitySeconds == null || validitySeconds === '') && data.user) {
      validitySeconds = data.user.temporary_token_validity_seconds;
    }
    if (validitySeconds == null || validitySeconds === '') {
      validitySeconds = data.temp_token_expires_in;
      if ((validitySeconds == null || validitySeconds === '') && data.user) {
        validitySeconds = data.user.temp_token_expires_in;
      }
    }
    if (validitySeconds != null && validitySeconds !== '') {
      let seconds = parseInt(validitySeconds, 10);
      if (!isNaN(seconds) && seconds > 0) {
        // Use login API temporary_token_validity_seconds as-is (capped only by max safety)
        validityMs = Math.min(seconds * 1000, this.TEMP_TOKEN_MAX_VALIDITY_MS);
      }
    }
    this.setTemporaryUserToken(token, validityMs);
    this.storeCompanyTokenFromLoginResponse(data);
  }

  storeCompanyTokenFromLoginResponse(data: any): void {
    let companyToken = this.extractCompanyTokenFromLoginResponse(data);
    let role = '';
    if (data && data.user && data.user.role) {
      role = data.user.role.toString();
    }
    if (role !== 'administrator') {
      this.clearAdministratorCompanyToken();
    }
    if (!companyToken) {
      return;
    }
    this.persistCompanyAccessToken(companyToken);
    if (role === 'administrator') {
      this.persistAdministratorCompanyToken(companyToken);
    }
  }

  persistAdministratorCompanyToken(token: string): void {
    if (!token) {
      return;
    }
    this.administratorCompanyTokenInMemory = token;
    // sessionStorage survives localStorage.clear() on company list
    sessionStorage.setItem(this.ADMIN_COMPANY_TOKEN_KEY, token);
    try {
      if (!localStorage.getItem('is_enc_on')) {
        localStorage.setItem('is_enc_on', this.eesdesService.set('1', 'is_enc_on'));
      }
      localStorage.setItem(this.ADMIN_COMPANY_TOKEN_KEY, this.eesdesService.set(token));
    } catch (e) {
    }
  }

  getAdministratorCompanyToken(): string {
    if (this.administratorCompanyTokenInMemory) {
      return this.administratorCompanyTokenInMemory;
    }
    let sessionToken = sessionStorage.getItem(this.ADMIN_COMPANY_TOKEN_KEY);
    if (sessionToken) {
      this.administratorCompanyTokenInMemory = sessionToken;
      return sessionToken;
    }
    try {
      let raw = localStorage.getItem(this.ADMIN_COMPANY_TOKEN_KEY);
      if (!raw) {
        return '';
      }
      let token = this.eesdesService.get(this.ADMIN_COMPANY_TOKEN_KEY, raw);
      if (token) {
        this.administratorCompanyTokenInMemory = token;
        sessionStorage.setItem(this.ADMIN_COMPANY_TOKEN_KEY, token);
        return token;
      }
    } catch (e) {
    }
    return '';
  }

  clearAdministratorCompanyToken(): void {
    this.administratorCompanyTokenInMemory = '';
    sessionStorage.removeItem(this.ADMIN_COMPANY_TOKEN_KEY);
    localStorage.removeItem(this.ADMIN_COMPANY_TOKEN_KEY);
  }

  private restoreAdministratorCompanyToken(): void {
    let sessionToken = sessionStorage.getItem(this.ADMIN_COMPANY_TOKEN_KEY);
    if (sessionToken) {
      this.administratorCompanyTokenInMemory = sessionToken;
      return;
    }
    try {
      let raw = localStorage.getItem(this.ADMIN_COMPANY_TOKEN_KEY);
      if (!raw) {
        return;
      }
      let token = this.eesdesService.get(this.ADMIN_COMPANY_TOKEN_KEY, raw);
      if (token) {
        this.administratorCompanyTokenInMemory = token;
        sessionStorage.setItem(this.ADMIN_COMPANY_TOKEN_KEY, token);
      }
    } catch (e) {
    }
  }

  isAdministratorRole(): boolean {
    try {
      let role = this.eesdesService.get('emp_role', localStorage.getItem('emp_role'));
      if (role === 'administrator') {
        return true;
      }
    } catch (e) {
    }
    try {
      let user = this.eesdesService.get('user_data', localStorage.getItem('user_data'));
      if (user && user.role === 'administrator') {
        return true;
      }
    } catch (e) {
    }
    return false;
  }

  persistUserSessionToken(token: string): void {
    if (!token) {
      return;
    }
    this.sessionUserTokenInMemory = token;
    sessionStorage.setItem(this.SESSION_USER_TOKEN_PLAIN_KEY, token);
    try {
      if (!localStorage.getItem('is_enc_on')) {
        localStorage.setItem('is_enc_on', this.eesdesService.set('1', 'is_enc_on'));
      }
      localStorage.setItem(this.SESSION_USER_TOKEN_KEY, this.eesdesService.set(token));
    } catch (e) {
      localStorage.setItem(this.SESSION_USER_TOKEN_KEY, token);
    }
  }

  getSessionUserToken(): string {
    if (this.sessionUserTokenInMemory) {
      return this.sessionUserTokenInMemory;
    }
    let plain = sessionStorage.getItem(this.SESSION_USER_TOKEN_PLAIN_KEY);
    if (plain) {
      this.sessionUserTokenInMemory = plain;
      return plain;
    }
    try {
      let raw = localStorage.getItem(this.SESSION_USER_TOKEN_KEY);
      if (!raw) {
        return '';
      }
      let token = this.eesdesService.get(this.SESSION_USER_TOKEN_KEY, raw);
      if (token) {
        this.sessionUserTokenInMemory = token;
        sessionStorage.setItem(this.SESSION_USER_TOKEN_PLAIN_KEY, token);
        return token;
      }
    } catch (e) {
    }
    return '';
  }

  clearSessionUserToken(): void {
    this.sessionUserTokenInMemory = '';
    sessionStorage.removeItem(this.SESSION_USER_TOKEN_PLAIN_KEY);
    localStorage.removeItem(this.SESSION_USER_TOKEN_KEY);
  }

  private restoreSessionUserToken(): void {
    let plain = sessionStorage.getItem(this.SESSION_USER_TOKEN_PLAIN_KEY);
    if (plain) {
      this.sessionUserTokenInMemory = plain;
      return;
    }
    try {
      let raw = localStorage.getItem(this.SESSION_USER_TOKEN_KEY);
      if (!raw) {
        return;
      }
      let token = this.eesdesService.get(this.SESSION_USER_TOKEN_KEY, raw);
      if (token) {
        this.sessionUserTokenInMemory = token;
        sessionStorage.setItem(this.SESSION_USER_TOKEN_PLAIN_KEY, token);
      }
    } catch (e) {
    }
  }

  extractCompanyApiToken(company: any): string {
    if (!company) {
      return '';
    }
    if (company.company_token) {
      return company.company_token;
    }
    if (company.company_api_token) {
      return company.company_api_token;
    }
    if (company.access_token) {
      return company.access_token;
    }
    if (company.api_token) {
      return company.api_token;
    }
    if (typeof company.token === 'string' && company.token) {
      return company.token;
    }
    return '';
  }

  findCompanyById(companies: any[], companyId: any): any {
    if (!companies || !companies.length || companyId == null || companyId === '') {
      return null;
    }
    let id = companyId.toString();
    for (let i = 0; i < companies.length; i++) {
      let company = companies[i];
      if (!company) {
        continue;
      }
      let rowCompanyId = company.company_id != null && company.company_id !== ''
        ? company.company_id
        : company.id;
      if (rowCompanyId != null && rowCompanyId.toString() === id) {
        return company;
      }
    }
    return null;
  }

  cacheCompanyToken(companyId: any, token: string): void {
    if (companyId == null || companyId === '' || !token) {
      return;
    }
    this.companyTokensById[companyId.toString()] = token;
    this.saveCompanyTokensCache();
  }

  cacheCompanyTokensFromCompanies(companies: any[]): void {
    if (!companies || !companies.length) {
      return;
    }
    for (let i = 0; i < companies.length; i++) {
      let company = companies[i];
      if (!company) {
        continue;
      }
      let companyId = company.company_id != null && company.company_id !== ''
        ? company.company_id
        : company.id;
      if (companyId == null || companyId === '') {
        continue;
      }
      let token = this.extractCompanyApiToken(company);
      if (token) {
        this.companyTokensById[companyId.toString()] = token;
      }
    }
    this.saveCompanyTokensCache();
  }

  cacheCompanyTokensFromResponse(data: any): void {
    if (!data) {
      return;
    }
    if (data.empdata && data.empdata.companies) {
      this.cacheCompanyTokensFromCompanies(data.empdata.companies);
    }
    if (data.vendordata && data.vendordata.companies) {
      this.cacheCompanyTokensFromCompanies(data.vendordata.companies);
    }
    if (data.companies) {
      this.cacheCompanyTokensFromCompanies(data.companies);
    }
    if (data.response && data.response.companies) {
      this.cacheCompanyTokensFromCompanies(data.response.companies);
    }
    if (data.response && Array.isArray(data.response)) {
      this.cacheCompanyTokensFromCompanies(data.response);
    }
  }

  getCachedCompanyApiToken(companyId: any): string {
    if (companyId == null || companyId === '') {
      return '';
    }
    if (!Object.keys(this.companyTokensById).length) {
      this.loadCompanyTokensCache();
    }
    let token = this.companyTokensById[companyId.toString()];
    return token ? token : '';
  }

  getCompanyApiToken(companyId: any, companyObj?: any): string {
    if (companyObj) {
      let tokenFromCompany = this.extractCompanyApiToken(companyObj);
      if (tokenFromCompany) {
        this.cacheCompanyToken(
          companyId != null && companyId !== ''
            ? companyId
            : (companyObj.company_id != null && companyObj.company_id !== '' ? companyObj.company_id : companyObj.id),
          tokenFromCompany
        );
        return tokenFromCompany;
      }
    }
    return this.getCachedCompanyApiToken(companyId);
  }

  clearCompanyTokensCache(): void {
    this.companyTokensById = {};
    localStorage.removeItem(this.COMPANY_TOKENS_CACHE_KEY);
  }

  isCompanyLicenseBlocked(licenseStatus: any): boolean {
    if (licenseStatus == null || licenseStatus === '') {
      return false;
    }
    let normalized = licenseStatus.toString().trim().toLowerCase();
    if (normalized === 'active' || normalized === '1' || normalized === 'valid') {
      return false;
    }
    if (normalized === 'expired' || normalized === 'inactive' || normalized === 'blocked' ||
      normalized === 'suspended' || normalized === '0' || normalized === 'false') {
      return true;
    }
    return false;
  }

  buildCompanyTokenLicensePayload(company: any, token: string): any {
    let payload: any = {
      status: token ? true : false,
      response: token ? token : '',
      message: ''
    };
    if (!company) {
      if (!token) {
        payload.message = 'Company token not available. Please login again.';
      }
      return payload;
    }
    if (company.license_warning === true || company.license_warning === '1') {
      payload.license_warning = true;
      payload.warning_message = company.warning_message ? company.warning_message : '';
    }
    if (this.isCompanyLicenseBlocked(company.license_status)) {
      payload.status = false;
      payload.license_status = company.license_status;
      payload.message = company.license_message ? company.license_message : (company.message ? company.message : 'This company cannot be accessed right now.');
      return payload;
    }
    if (!token) {
      payload.status = false;
      payload.message = company.message ? company.message : 'Company token not available. Please login again.';
    }
    return payload;
  }

  persistCompanyAccessToken(token: string): void {
    if (!token) {
      return;
    }
    localStorage.setItem('access_token', this.eesdesService.set(token));
  }

  private saveCompanyTokensCache(): void {
    try {
      localStorage.setItem(this.COMPANY_TOKENS_CACHE_KEY, this.eesdesService.set(JSON.stringify(this.companyTokensById)));
    } catch (e) {
    }
  }

  private loadCompanyTokensCache(): void {
    try {
      let raw = localStorage.getItem(this.COMPANY_TOKENS_CACHE_KEY);
      if (!raw) {
        this.companyTokensById = {};
        return;
      }
      let parsed = this.eesdesService.get(this.COMPANY_TOKENS_CACHE_KEY, raw);
      if (parsed && typeof parsed === 'object') {
        this.companyTokensById = parsed;
      } else if (typeof parsed === 'string') {
        this.companyTokensById = JSON.parse(parsed);
      } else {
        this.companyTokensById = {};
      }
    } catch (e) {
      this.companyTokensById = {};
    }
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3185250503/src/src/app/shared/services/auth-session.service.ts